import React, { useEffect, useMemo, useState } from "react";
import { Tag, TextField } from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import { ProgrammingStructure } from "@models/ISchema";
import CustomModelSelector from "@common/components/custom-model-selector";
import CampusCodeSelector from "@modules/programming-structures/components/campus-code-selector";
import styles from "./programingStructuresForm.module.scss";

type ProgrammingStructuresFormMode = "edit" | "readOnly" | "campusesOnly";

interface ProgrammingStructuresFormProps {
  scenario: string;
  programmingStructure?: ProgrammingStructure;
  mode: ProgrammingStructuresFormMode;
  onChange?: (formState: { code: string; name: string; subject: any; campuses: any[] }) => void;
  onValidForm?: (isValid: boolean) => void;
}

const getTextWithoutSpaces = (text: string) => text.replace(/\s/g, "");

const ProgrammingStructuresForm = ({
  scenario,
  programmingStructure,
  mode,
  onChange,
  onValidForm,
}: ProgrammingStructuresFormProps) => {
  const { t } = useTranslation();
  const [code, setCode] = useState(programmingStructure?.code ?? "");
  const [name, setName] = useState(programmingStructure?.name ?? "");
  const [selectedSubject, setSelectedSubject] = useState(null);
  /**
   * A Map, not a plain object: campus codes can be numeric strings, and object
   * keys that look like integers iterate in numeric order before the rest. The
   * Map preserves insertion order for every code, which is what keeps the tag
   * list stable after a bulk selection.
   */
  const [selectedCampuses, setSelectedCampuses] = useState<Map<string, string>>(new Map());

  const handleDeleteCampus = (campusCode: string) => {
    setSelectedCampuses(prev => {
      const newSelectedCampuses = new Map(prev);
      newSelectedCampuses.delete(campusCode);

      return newSelectedCampuses;
    });
  };

  const handleSelectCampus = (campusCode: string) => {
    setSelectedCampuses(prev => {
      if (prev.has(campusCode)) return prev;

      return new Map(prev).set(campusCode, campusCode);
    });
  };

  /**
   * Additive by construction: setting a key already present leaves it in place,
   * so saved campuses keep both their position and their locked rendering.
   */
  const handleSelectAllCampuses = (campusCodes: string[]) => {
    setSelectedCampuses(prev => {
      const newSelectedCampuses = new Map(prev);
      campusCodes.forEach(campusCode => newSelectedCampuses.set(campusCode, campusCode));

      return newSelectedCampuses;
    });
  };

  const adaptedCampusesPayload = useMemo(() => {
    return Array.from(selectedCampuses.values());
  }, [selectedCampuses]);

  const baselineCampusCodes = useMemo(() => {
    return new Set(programmingStructure?.campusCodes ?? []);
  }, [programmingStructure]);

  /**
   * Campuses already saved on the structure lead the list, session additions
   * follow. The split reads from the same baseline lookup that decides the
   * remove icon, so a tag can never be locked but sorted as an addition.
   */
  const orderedCampusCodes = useMemo(() => {
    const savedCampusCodes = adaptedCampusesPayload.filter(campusCode =>
      baselineCampusCodes.has(campusCode),
    );
    const addedCampusCodes = adaptedCampusesPayload.filter(
      campusCode => !baselineCampusCodes.has(campusCode),
    );

    return [...savedCampusCodes, ...addedCampusCodes];
  }, [adaptedCampusesPayload, baselineCampusCodes]);

  const isReadOnly = mode !== "edit";
  const isCampusSelectorDisabled = mode === "readOnly";

  useEffect(() => {
    onValidForm?.(!!code && !!name && !!selectedSubject && selectedCampuses.size > 0);

    onChange?.({
      code: getTextWithoutSpaces(code),
      name: name?.trim(),
      subject: selectedSubject?.self,
      campuses: adaptedCampusesPayload,
    });

    return () => {
      onValidForm?.(false);
      onChange?.({ code: "", name: "", subject: null, campuses: [] });
    };
  }, [code, name, selectedSubject, adaptedCampusesPayload]);

  useEffect(() => {
    if (programmingStructure) {
      setCode(programmingStructure.code);
      setName(programmingStructure.name);
      // Seeded first and in the order the structure returns them, so the saved
      // campuses lead the Map's insertion order.
      setSelectedCampuses(
        new Map(programmingStructure.campusCodes.map(campusCode => [campusCode, campusCode])),
      );

      const subjectCode = programmingStructure.subjectCodes?.[0] ?? null;
      setSelectedSubject({
        label: subjectCode,
        value: subjectCode,
        self: {
          code: subjectCode,
        },
      });
    }
  }, [programmingStructure]);

  return (
    <section className={styles.programmingStructuresForm}>
      <div className={styles.programmingStructuresForm_control}>
        <TextField
          className={styles.programmingStructuresForm_field}
          label={t("programming-structures.form.code")}
          placeholder={t("programming-structures.form.code-placeholder")}
          value={code}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setCode(getTextWithoutSpaces(e?.target?.value?.trim()))
          }
          disabled={isReadOnly}
        />
        <TextField
          className={styles.programmingStructuresForm_field}
          label={t("programming-structures.form.name")}
          placeholder={t("programming-structures.form.name-placeholder")}
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e?.target?.value)}
          disabled={isReadOnly}
        />
      </div>

      <CustomModelSelector
        key={programmingStructure?.id && selectedSubject ? "saved-selector" : undefined}
        label={t("advanced-search.instructor.forms.select-wrapper.subject.label")}
        placeholder={t("advanced-search.instructor.forms.select-wrapper.subject.placeholder")}
        selectedOption={selectedSubject}
        scenario={scenario}
        onChange={setSelectedSubject}
        isDisabled={isReadOnly}
        customModelCode="SUBJECTS"
        getOptionLabel={option => `${option?.code} | ${option?.label}`}
      />

      <div className={styles.programmingStructuresForm_campuses}>
        <CampusCodeSelector
          scenario={scenario}
          selectedCampusCodes={adaptedCampusesPayload}
          isDisabled={isCampusSelectorDisabled}
          onSelectCampus={handleSelectCampus}
          onSelectAllCampuses={handleSelectAllCampuses}
        />

        {!!orderedCampusCodes.length && (
          <div className={styles.programmingStructuresForm_selectedCampuses}>
            {orderedCampusCodes.map(campus => {
              const isLockedCampus = mode === "campusesOnly" && baselineCampusCodes.has(campus);
              const isRemovableCampus = mode === "campusesOnly" ? !isLockedCampus : mode === "edit";

              return (
                <Tag
                  key={`campus-tag-${campus}`}
                  icon={isRemovableCampus ? "close" : undefined}
                  onIconClick={() => isRemovableCampus && handleDeleteCampus(campus)}
                >
                  {campus}
                </Tag>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default ProgrammingStructuresForm;
